import "./NftCard.scss";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import dollarIcon from "../../assets/dollarNftCircle.svg";
import solanaLogo from "../../assets/solanaLogo.svg";
import SellingInfoModal from "./SellingInfoModal/SellingInfoModal";
import { vaultStore } from "../../contexts/vaultStore";
import { IDetailNftData } from "../../common/interfaces/common.interface";
import magicEdenLogo from "../../assets/magicEdenLogo.svg";
import link from "../../assets/linkWhite.svg";
import {
  CancellationType,
  OfferStatus,
  ProposalAction,
} from "../../common/enums/proposals.enums";
import {
  EMPTY_STRING,
  MESSAGE_TYPE,
} from "../../common/constants/common.constants";
import { getClubVault } from "../../solana/methods/clubs";
import { cancelEscrow } from "../../solana/methods/proposals";
import { createNotification } from "../../utilities/notification";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { toggleModalOpenClass } from "../../utilities/helpers";
import CancellationModal from "../ClubDetails/OverviewClub/Proposals/ProposalsItem/CancellationModal/CancellationModal";
import { CLUB_DETAILS, OFFER } from "../../common/constants/routes.constants";
import { REACT_APP_FRONT_BASE_URL } from "../../solana/common/utilities";
import { useParams } from "react-router";
import { getTreasuryAddressFroClub } from "../../solana/common/helpers/common.helpers";
import { NftListing } from "../../common/enums/common.enums";
import { cancelSolseaProposal } from "../../solana/common/helpers/solsea.helpers";
import { getNftDataFromSolsea } from "../../api/solsea.api";
import { PublicKey } from "@solana/web3.js";
import { SOLSEA_DEVNET_WEBSITE } from "../../api/utilities/solsea.urls";
import { clubsStore } from "../../contexts/clubStore";

interface INftCardProps {
  nft: IDetailNftData;
  isSummaryStep?: boolean;
  votedWithNft?: boolean;
  isSellable?: boolean;
  isOnSale?: boolean;
  isOnBid?: boolean;
  sellNft?: (nft: IDetailNftData) => void;
}

const NftCard: React.FC<INftCardProps> = ({
  nft,
  votedWithNft,
  isSellable,
  isOnSale,
  isOnBid,
  isSummaryStep,
  sellNft,
}) => {
  const [showTradeButton, toggleShowTradeButton] = useState(false);
  const [isSellingModalInfoVisible, toggleIsSellingInfoModalVisible] =
    useState(false);

  const [isCancelSaleModalVisible, toggleIsCancelSaleModalVisible] =
    useState(false);

  const params = useParams();

  useEffect(() => {
    if (params["clubAddress"]) {
      void setTreasuryPubkey();
    }
  }, []);

  const setTreasuryPubkey = async () => {
    setClubTreasury(await getTreasuryAddressFroClub(params["clubAddress"]!));
  };

  const { clubBasicInfo, treasury, setVault, vault } = clubsStore();
  const wallet = useAnchorWallet();
  const [clubTreasury, setClubTreasury] = useState(treasury?.treasury);

  const { updateNftsAfterAction } = vaultStore();

  const relatedNftInStore = vaultStore((state) =>
    (isOnSale ? state.nftsOnSale : state.nftsOnBids).find(
      (nftOnSale) =>
        nftOnSale.mint.toString() === nft.mint.toString() &&
        nftOnSale.price === nft.price
    )
  );

  const cancelNftTrade = useCallback(
    async (e, reasonId: number, customMessage?: string) => {
      try {
        if (
          relatedNftInStore &&
          relatedNftInStore.status === OfferStatus.listedOnSolsea
        ) {
          const solseaData = await getNftDataFromSolsea(nft.mint.toString());
          if (!solseaData || !solseaData.listed)
            throw new Error("NFT sold on SolSea!");
          await cancelSolseaProposal(
            clubBasicInfo ? clubBasicInfo.name : params["clubName"]!,
            relatedNftInStore.proposalPubkey,
            relatedNftInStore.offer,
            new PublicKey(solseaData.escrowKey),
            nft.mint,
            wallet!,
            clubTreasury!,
            {
              proposalAddress: relatedNftInStore.proposalPubkey.toString(),
              reasonId,
              signedTransaction: EMPTY_STRING,
              signerAddress: wallet!.publicKey.toString(),
              customReason: customMessage,
            }
          );
        } else if (
          relatedNftInStore &&
          relatedNftInStore.status === OfferStatus.initialised &&
          (isOnSale || isOnBid)
        ) {
          await cancelEscrow(
            clubBasicInfo ? clubBasicInfo.name : params["clubName"]!,
            wallet!,
            isOnBid ? ProposalAction.BuyNft : ProposalAction.SellNft,
            relatedNftInStore.offer,
            relatedNftInStore.makerOfferedTokens,
            relatedNftInStore.maker,
            relatedNftInStore.proposalPubkey,
            clubTreasury!,
            relatedNftInStore.escrowOfferedTokensAccount,
            {
              reasonId,
              signedTransaction: EMPTY_STRING,
              signerAddress: wallet!.publicKey.toString(),
              customReason: customMessage,
              proposalAddress: relatedNftInStore.proposalPubkey.toString(),
            }
          );
        }
        if (vault) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          const updatedVault = await getClubVault(clubBasicInfo.name);
          setVault({
            nfts: [...updatedVault.nfts],
            vaultAddress: updatedVault.vaultAddress,
          });
        }
        updateNftsAfterAction(
          relatedNftInStore!,
          isOnBid ? NftListing.Bids : NftListing.Sales
        );
        if (relatedNftInStore?.isMagicEden) {
          createNotification(
            MESSAGE_TYPE.SUCCESS,
            "NFT successfully unlisted from Magic Eden"
          );
        }
      } catch (error: any) {
        createNotification(
          MESSAGE_TYPE.ERROR,
          "Failed to unlist NFT",
          error.message
        );
      } finally {
        toggleIsCancelSaleModalVisible(false);
        toggleModalOpenClass(false);
      }
    },
    [
      clubBasicInfo,
      wallet,
      nft,
      relatedNftInStore,
      clubTreasury,
      isOnBid,
      isOnSale,
    ]
  );

  const getCancellationType = useMemo(() => {
    if (relatedNftInStore?.status === OfferStatus.initialised && isOnBid) {
      return CancellationType.BUY_P2P;
    }
    if (isOnSale) {
      return relatedNftInStore?.status === OfferStatus.initialised
        ? CancellationType.SELL_P2P
        : CancellationType.SELL_SOLSEA;
    }
    return CancellationType.SELL_P2P;
  }, [relatedNftInStore, isOnBid, isOnSale]);

  const copyOfferAddress = useCallback(() => {
    try {
      if (relatedNftInStore?.status === OfferStatus.listedOnSolsea) {
        openNftOnSolsea();
      } else {
        navigator.clipboard.writeText(
          `${
            process.env.REACT_APP_FRONT_BASE_URL
          }${OFFER}/${relatedNftInStore?.proposalPubkey.toString()}/${
            clubBasicInfo?.address ?? params["clubAddress"]
          }`
        );
        createNotification(
          MESSAGE_TYPE.SUCCESS,
          "Offer address copied to clipboard"
        );
      }
    } catch (error) {
      console.log(error);

      createNotification(MESSAGE_TYPE.ERROR, "Failed to copy offer address!");
    }
  }, []);

  const openNftOnSolsea = useCallback(() => {
    window.open(`${SOLSEA_DEVNET_WEBSITE}n/${nft.mint.toString()}`, "_blank");
  }, [nft]);

  const redirectToNewTab = useCallback(() => {
    try {
      const clubAddress = params["clubAddress"];
      window.location.replace(
        `${REACT_APP_FRONT_BASE_URL}${CLUB_DETAILS}/${clubAddress}?nftMint=${nft.mint.toString()}`
      );
    } catch (error) {}
  }, [params, nft]);

  return (
    <div
      onMouseEnter={() => toggleShowTradeButton(true)}
      onMouseLeave={() => toggleShowTradeButton(false)}
      className={`${votedWithNft ? "nft-card nft-card__voted" : "nft-card"}`}
    >
      {(isOnBid || isOnSale) && showTradeButton && (
        <div className="nft-card__link-icon">
          <img
            className="nft-card__link-icon-image"
            src={relatedNftInStore?.isMagicEden ? magicEdenLogo : link}
            alt="link"
            onClick={() => copyOfferAddress()}
          />
        </div>
      )}
      {isCancelSaleModalVisible && (
        <CancellationModal
          closeModal={() => toggleIsCancelSaleModalVisible(false)}
          cancellationType={getCancellationType}
          submitFunction={cancelNftTrade}
        />
      )}

      <div className="nft-card__nft-image-wrapper">
        <img className="nft-card__nft-image" src={nft.nftUrl} alt={nft.name} />
        {showTradeButton && isSellable && (
          <button
            onClick={() =>
              !isSummaryStep ? redirectToNewTab() : sellNft!(nft)
            }
            className="nft-card__sell-button"
          >
            <img src={dollarIcon} alt="dollarIcon" />
            Sell
          </button>
        )}
        {showTradeButton && (isOnSale || isOnBid) && (
          <button
            onClick={() => toggleIsCancelSaleModalVisible(true)}
            className="nft-card__cancel-button"
          >
            Cancel
          </button>
        )}
      </div>
      <div className="nft-card__info">
        <h3 className="nft-card__nft-name">{nft.name}</h3>
        <h3 className="nft-card__collection-name">{nft.collection?.name}</h3>
        {isOnBid && relatedNftInStore && (
          <div className="nft-card__bid-price">
            <img src={solanaLogo} alt="Solana" />
            <p className="nft-card__price">{relatedNftInStore.price!}</p>
          </div>
        )}
      </div>
      {isOnSale && (
        <div className="nft-card__sale">
          {isSellingModalInfoVisible && relatedNftInStore && (
            <SellingInfoModal
              sellingPrice={nft.price!}
              youWillGet={relatedNftInStore.youWillGet!}
            />
          )}
          <div className="nft-card__sale-price">
            <img src={solanaLogo} alt="solanaLogo" />
            <p className="nft-card__price">{nft.price}</p>
          </div>
          <button
            onMouseEnter={() => toggleIsSellingInfoModalVisible(true)}
            onMouseLeave={() => toggleIsSellingInfoModalVisible(false)}
            className="nft-card__details-button"
          >
            Details
          </button>
        </div>
      )}
    </div>
  );
};

export default NftCard;
